/*!
 * KODIAK SPORTS LLC - PROPRIETARY SOFTWARE
 * Copyright 2026 Kodiak Sports, LLC. All rights reserved.
 *
 * Synthetic Turf Halo Designer - live canvas preview + configurator + pricing.
 * Halo render geometry derived from the Facility Design Matrix baseball field engine
 * (clip-based ring - no triangle artifact). Dimension callouts match the field designer.
 *
 * 2026-07-07 parity update (matches the FDM designer):
 *   - Wings are 20' long on BOTH 5' and 10' halos (only the end width changes).
 *   - 26' dia dimension is a true diameter through the center, arrowheads on the
 *     ring's inner edge; label sits below the batter's boxes.
 *   - Wing length dim sits on the FOUL-LINE side of the wing.
 *   - Outer Radius leader removed; dimension callouts all Yellow.
 *   - Field-look preview: solid green field, clay home-plate circle, chalk foul
 *     lines that terminate at the batter's boxes (never touching home plate),
 *     4x6 batter's boxes, white home plate pentagon.
 * Configurator / pricing / add-to-cart logic is UNCHANGED from the prior version.
 *
 * UNAUTHORIZED COPYING, REPRODUCTION, MODIFICATION, DISTRIBUTION, OR USE OF THIS CODE OR
 * ITS DESIGN - IN WHOLE OR IN PART - IS STRICTLY PROHIBITED WITHOUT THE EXPRESS WRITTEN
 * CONSENT OF KODIAK SPORTS, LLC.  Kodiak Sports, LLC | Plano, TX | www.kodiaksports.com
 */
(function () {
  'use strict';

  var root = document.getElementById('halo-designer');
  if (!root) return;

  // ---- Config injected by the Liquid template (window.HALO_CFG) ----
  var CFG = window.HALO_CFG || {};
  var VARIANTS = CFG.variants || [];
  var ADDON    = CFG.addon    || {};
  var PRICE    = CFG.price     || {};
  var COLORS   = CFG.colors    || [];          // shared list [{name,hex}] for turf + letters + outline
  var COLOR_HEX = {};
  COLORS.forEach(function (c) { COLOR_HEX[c.name] = c.hex; });
  if (!COLORS.length && CFG.colorHex) { COLOR_HEX = CFG.colorHex; }
  var moneyFmt = CFG.moneyFormat || '${{amount}}';

  // ---- Designer state ----
  var state = {
    size:    (VARIANTS[0] && VARIANTS[0].size)    || "5' - No Wings",
    color:   (VARIANTS[0] && VARIANTS[0].color)   || 'Green',
    padding: (VARIANTS[0] && VARIANTS[0].padding) || 'Unitary/No Foam Pad',
    mode:    'letter',
    text:    'EAGLES',
    letterColor: '#ffffff',
    twoColor: false,
    outlineColor: '#c0202a',
    wingLogo1: false,
    wingLogo3: false,
    glue: false
  };

  // ---- Helpers ----
  function $(sel) { return root.querySelector(sel); }
  function $all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function isTen()   { return state.size.indexOf("10'") === 0; }
  function hasWings(){ return state.size.indexOf('With Wings') !== -1; }
  function depth()   { return isTen() ? 10 : 5; }
  function letterCount() { if (state.mode !== 'letter') return 0; return (state.text || '').replace(/\s+/g, '').length; }
  function logoCount() { var n = 0; if (state.mode === 'logo') n += 1; if (hasWings() && state.wingLogo1) n += 1; if (hasWings() && state.wingLogo3) n += 1; return n; }
  function glueQty() { return state.glue ? (isTen() ? 2 : 1) : 0; }
  function money(amount) { var v = (Math.round(amount * 100) / 100).toFixed(2); var p = v.split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); v = p.join('.'); return moneyFmt.replace('{{amount}}', v).replace('{{ amount }}', v); }
  function findVariant() { for (var i = 0; i < VARIANTS.length; i++) { var v = VARIANTS[i]; if (v.size === state.size && v.color === state.color && v.padding === state.padding) return v; } return null; }
  function letterRate() { return isTen() ? (PRICE.letter10 || 550) : (PRICE.letter5 || 450); }

  // ============================================================
  //  CANVAS RENDER (clip-based ring + wings + content + dimensions)
  // ============================================================
  function drawLogoBadge(x, ccx, ccy, box) {
    x.save();
    x.beginPath(); x.arc(ccx, ccy, box * 0.5, 0, Math.PI * 2);
    x.fillStyle = 'rgba(255,255,255,0.92)'; x.fill();
    x.lineWidth = Math.max(1, box * 0.04); x.strokeStyle = 'rgba(0,0,0,0.35)'; x.stroke();
    x.fillStyle = '#1b2b4b';
    x.font = '700 ' + Math.max(7, Math.round(box * 0.22)) + "px 'Barlow Condensed', Arial, sans-serif";
    x.textAlign = 'center'; x.textBaseline = 'middle';
    x.fillText('LOGO', ccx, ccy);
    x.restore();
  }

  // Arc lettering with AUTO-SCALE: letters cap at the proportional height (maxFontPx) for short
  // words, and shrink to fit the ring's usable arc when there are many. Spacing stays even.
  function arcText(x, cx, cy, r, text, fill, stroke, maxFontPx) {
    if (!text) return;
    var n = text.length;
    var MAX_SPAN = Math.PI * 1.15;           // usable lower arc (keeps letters off the opening/wings)
    var step = (maxFontPx * 0.86) / r;       // angular slot per letter at full height
    var fontPx = maxFontPx;
    if (n > 1 && step * (n - 1) > MAX_SPAN) {
      var f = MAX_SPAN / (step * (n - 1));   // overflow -> shrink letters + spacing to fit
      step *= f; fontPx = maxFontPx * f;
    }
    var span = step * Math.max(0, n - 1);
    var half = span / 2;
    x.save();
    x.font = 'bold ' + Math.max(7, Math.round(fontPx)) + 'px Arial Black, Arial, sans-serif';
    x.textAlign = 'center'; x.textBaseline = 'middle';
    for (var i = 0; i < n; i++) {
      var a = n > 1 ? (Math.PI / 2 + half) - span * (i / (n - 1)) : Math.PI / 2;
      var ch = text[i];
      x.save();
      x.translate(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      x.rotate(a - Math.PI / 2);
      if (stroke) { x.lineWidth = Math.max(2, fontPx * 0.18); x.strokeStyle = stroke; x.lineJoin = 'round'; x.strokeText(ch, 0, 0); }
      x.fillStyle = fill; x.fillText(ch, 0, 0);
      x.restore();
    }
    x.restore();
  }

  function renderPreview() {
    var cv = $('#halo-canvas');
    if (!cv || !cv.getContext) return;
    var x = cv.getContext('2d');
    var W = cv.width, H = cv.height;
    x.clearRect(0, 0, W, H);
    // Field-look: solid green field turf (matches the FDM designer, 2026-07-07)
    x.fillStyle = '#3a8038'; x.fillRect(0, 0, W, H);

    var d = depth();
    var hc = COLOR_HEX[state.color] || '#101012';
    var innerR = 13, outerR = 13 + d, letterH = d === 10 ? 7 : 3;
    // Wings: 20' long on BOTH depths - only the end width changes (2026-07-07)
    var wingLen = 20, wingW = d === 10 ? 10 : 5, off = 2;
    var wings = hasWings();
    var a1B = -Math.PI / 4, a3B = -3 * Math.PI / 4;

    var SHOW_DIMS = true;
    var FILL = 0.99;
    var leadFt = 5;
    function perpOf(ang) { var u = { x: Math.cos(ang), y: Math.sin(ang) }; var p = { x: u.y, y: -u.x }; if (p.x * u.x < 0) { p.x = -p.x; p.y = -p.y; } return { u: u, perp: p }; }
    var pts = [[outerR, 0], [-outerR, 0], [0, outerR],
      [outerR * Math.cos(a1B), outerR * Math.sin(a1B)], [outerR * Math.cos(a3B), outerR * Math.sin(a3B)]];
    if (wings) {
      [a1B, a3B].forEach(function (ang) {
        var g = perpOf(ang);
        [innerR, innerR + wingLen].forEach(function (a) {
          [off, off + wingW].forEach(function (c) { pts.push([g.u.x * a + g.perp.x * c, g.u.y * a + g.perp.y * c]); });
        });
      });
    }
    if (SHOW_DIMS) {
      var ltA = Math.PI * 0.74, textFt = 9;
      pts.push([(outerR + leadFt) * Math.cos(ltA) - textFt, (outerR + leadFt) * Math.sin(ltA)]);
      if (wings) {
        [a1B, a3B].forEach(function (ang) {
          var g2 = perpOf(ang), mid = innerR + wingLen / 2;
          pts.push([g2.u.x * (innerR + wingLen + 4) + g2.perp.x * (off + wingW), g2.u.y * (innerR + wingLen + 4) + g2.perp.y * (off + wingW)]);
        });
      }
    }
    var minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
    pts.forEach(function (p) { if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0]; if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1]; });
    var halfW = Math.max(Math.abs(minX), Math.abs(maxX));
    var contentH = maxY - minY;
    var s = FILL * Math.min((W * 0.5) / halfW, H / contentH);
    var cx = W * 0.5, cy = H * 0.5 - ((minY + maxY) / 2) * s;

    // RING: outer disk minus the top opening (clip-based - no triangle)
    x.save();
    x.beginPath(); x.arc(cx, cy, outerR * s, 0, Math.PI * 2); x.clip();
    (function () {
      var u1c = { x: Math.cos(a1B), y: Math.sin(a1B) }, u3c = { x: Math.cos(a3B), y: Math.sin(a3B) };
      var p1 = { x: u1c.y, y: -u1c.x }; if (p1.x * u1c.x < 0) { p1.x = -p1.x; p1.y = -p1.y; }
      var p3 = { x: u3c.y, y: -u3c.x }; if (p3.x * u3c.x < 0) { p3.x = -p3.x; p3.y = -p3.y; }
      var far = outerR * 4;
      var w0 = [cx + p1.x * off * s, cy + p1.y * off * s], w3 = [cx + p3.x * off * s, cy + p3.y * off * s];
      x.beginPath(); x.rect(0, 0, W, H);
      x.moveTo(w0[0], w0[1]);
      x.lineTo(w0[0] + u1c.x * far * s, w0[1] + u1c.y * far * s);
      x.lineTo(w3[0] + u3c.x * far * s, w3[1] + u3c.y * far * s);
      x.lineTo(w3[0], w3[1]);
      x.closePath();
      x.clip('evenodd');
    })();
    x.fillStyle = hc; x.fillRect(0, 0, W, H);
    x.restore();

    // Home-plate circle: clay dirt (field-look, 2026-07-07; was cream)
    x.beginPath(); x.arc(cx, cy, innerR * s, 0, Math.PI * 2); x.fillStyle = '#b06a3a'; x.fill();

    function wgeo(ang) {
      var u = { x: Math.cos(ang), y: Math.sin(ang) };
      var perp = { x: u.y, y: -u.x }; if (perp.x * u.x < 0) { perp.x = -perp.x; perp.y = -perp.y; }
      var a0 = innerR, a1 = innerR + wingLen;
      var P = function (a, c) { return [cx + (u.x * a + perp.x * c) * s, cy + (u.y * a + perp.y * c) * s]; };
      return { u: u, perp: perp, a0: a0, a1: a1, P: P };
    }
    function wing(ang, hasLogo) {
      var G = wgeo(ang);
      var C = [G.P(G.a0, off), G.P(G.a1, off), G.P(G.a1, off + wingW), G.P(G.a0, off + wingW)];
      x.beginPath();
      C.forEach(function (p, i) { i ? x.lineTo(p[0], p[1]) : x.moveTo(p[0], p[1]); });
      x.closePath(); x.fillStyle = hc; x.fill();
      if (hasLogo) {
        var lcc = G.P(G.a0 + wingLen / 2, off + wingW / 2);
        drawLogoBadge(x, lcc[0], lcc[1], Math.min(wingLen, wingW) * 0.78 * s);
      }
    }
    if (wings) { wing(a3B, state.wingLogo3); wing(a1B, state.wingLogo1); }

    if (state.mode === 'logo') {
      drawLogoBadge(x, cx, cy + (innerR + d * 0.5) * s, (outerR - innerR) * 1.3 * s);
    } else {
      arcText(x, cx, cy, (innerR + d * 0.575) * s, String(state.text || '').toUpperCase().slice(0, 16),
        state.letterColor || '#ffffff', state.twoColor ? state.outlineColor : '', letterH * s * 1.3);
    }

    // Field-look foreground (ported from the FDM field designer, 2026-07-07):
    // chalk foul lines terminate at the batter's box outline - the foul line
    // crosses the 4x6 box top edge 3*sqrt2 ft from the plate center - and
    // never touch home plate. Chalk draws OVER the halo, like the real field.
    (function () {
      var far = (outerR + wingLen + wingW) * 4 * s;
      var fl0 = 3 * Math.SQRT2 * s;
      x.save();
      x.strokeStyle = '#f6f6f1'; x.lineWidth = Math.max(2, 0.5 * s); x.lineJoin = 'round';
      [a1B, a3B].forEach(function (ang) {
        x.beginPath();
        x.moveTo(cx + Math.cos(ang) * fl0, cy + Math.sin(ang) * fl0);
        x.lineTo(cx + Math.cos(ang) * far, cy + Math.sin(ang) * far);
        x.stroke();
      });
      x.lineWidth = Math.max(2, 0.4 * s);
      var boxRect = function (bx0, bx1, bz0, bz1) {
        x.beginPath();
        x.moveTo(cx + bx0 * s, cy - bz0 * s); x.lineTo(cx + bx1 * s, cy - bz0 * s);
        x.lineTo(cx + bx1 * s, cy - bz1 * s); x.lineTo(cx + bx0 * s, cy - bz1 * s);
        x.closePath(); x.stroke();
      };
      boxRect(0.71, 4.71, -3, 3); boxRect(-4.71, -0.71, -3, 3);
      var hp = [[-0.708, 0.71], [0.708, 0.71], [0.708, 0], [0, -0.71], [-0.708, 0]];
      x.beginPath();
      hp.forEach(function (p, i) { var px = cx + p[0] * s, py = cy - p[1] * s; i ? x.lineTo(px, py) : x.moveTo(px, py); });
      x.closePath(); x.fillStyle = '#f4f4ee'; x.fill();
      x.restore();
    })();

    // ===== dimension callouts =====
    if (SHOW_DIMS) {
      x.save();
      x.strokeStyle = '#f2c200'; x.fillStyle = '#f2c200'; x.lineWidth = 1; // dim system all Yellow (2026-07-07)
      x.font = 'bold 13px Arial, sans-serif'; x.textAlign = 'center'; x.textBaseline = 'middle';
      var lead = leadFt * s;
      var ah = function (tx, ty, fx, fy) { var a = Math.atan2(ty - fy, tx - fx), L = 6; x.beginPath(); x.moveTo(tx, ty); x.lineTo(tx - L * Math.cos(a - 0.42), ty - L * Math.sin(a - 0.42)); x.moveTo(tx, ty); x.lineTo(tx - L * Math.cos(a + 0.42), ty - L * Math.sin(a + 0.42)); x.stroke(); };
      var dim = function (ax, ay, bx, by) { x.beginPath(); x.moveTo(ax, ay); x.lineTo(bx, by); x.stroke(); ah(ax, ay, bx, by); ah(bx, by, ax, ay); };
      var leader = function (tx, ty, lx, ly) { x.beginPath(); x.moveTo(lx, ly); x.lineTo(tx, ty); x.stroke(); ah(tx, ty, lx, ly); };
      var ar = Math.PI * 0.2, ex = cx + innerR * s * Math.cos(ar), ey = cy + innerR * s * Math.sin(ar);
      x.beginPath(); x.moveTo(cx, cy); x.lineTo(ex, ey); x.stroke(); ah(ex, ey, cx, cy);
      x.fillText("Radius " + innerR + "'", cx + innerR * s * 0.74 * Math.cos(ar), cy + innerR * s * 0.74 * Math.sin(ar) - 8);
      // 26' dia: TRUE diameter through the center - arrowheads land exactly on
      // the ring's inner edge; label below the batter's boxes (2026-07-07).
      dim(cx - innerR * s, cy, cx + innerR * s, cy); x.fillText((innerR * 2) + "' diameter", cx, cy + 5.5 * s);
      if (state.mode === 'letter' && state.text) {
        var la = Math.PI * 0.74, ltx = cx + Math.cos(la) * (innerR + d * 0.575) * s, lty = cy + Math.sin(la) * (innerR + d * 0.575) * s, llx = cx + Math.cos(la) * (outerR * s + lead), lly = cy + Math.sin(la) * (outerR * s + lead);
        leader(ltx, lty, llx, lly); x.textAlign = 'right'; x.fillText(letterH + "'H letters", llx - 5, lly + 2); x.textAlign = 'center';
      }
      if (wings) {
        var wingDims = function (ang) {
          var G = wgeo(ang), u = G.u, perp = G.perp;
          // Length dim on the FOUL-LINE side of the wing (2026-07-07; was on
          // the opening side, which read as measuring the wrong edge).
          var s1 = G.P(G.a0, off), s2 = G.P(G.a1, off);
          var o1 = [s1[0] - perp.x * 9, s1[1] - perp.y * 9], o2 = [s2[0] - perp.x * 9, s2[1] - perp.y * 9];
          dim(o1[0], o1[1], o2[0], o2[1]);
          x.save(); x.translate((o1[0] + o2[0]) / 2 - perp.x * 18, (o1[1] + o2[1]) / 2 - perp.y * 18); var lr = ang; if (lr < -Math.PI / 2 || lr > Math.PI / 2) lr += Math.PI; x.rotate(lr); x.fillText((Math.round(wingLen * 100) / 100) + "'", 0, 0); x.restore();
          var e1 = G.P(G.a1, off), e2 = G.P(G.a1, off + wingW);
          var f1 = [e1[0] + u.x * 9, e1[1] + u.y * 9], f2 = [e2[0] + u.x * 9, e2[1] + u.y * 9];
          dim(f1[0], f1[1], f2[0], f2[1]); x.fillText(wingW.toFixed(0) + "'", (f1[0] + f2[0]) / 2 + u.x * 12, (f1[1] + f2[1]) / 2 + u.y * 12);
        };
        wingDims(a3B); wingDims(a1B);
      }
      x.restore();
    }
  }

  // ============================================================
  //  PRICING + UI SYNC
  // ============================================================
  function setText(sel, txt) { var el = $(sel); if (el) el.textContent = txt; }
  function setRow(sel, show, label, amount) {
    var el = $(sel); if (!el) return;
    el.style.display = show ? 'flex' : 'none';
    var l = el.querySelector('.hd-br-label'); var a = el.querySelector('.hd-br-amt');
    if (l) l.textContent = label; if (a) a.textContent = amount;
  }
  function recalc() {
    var v = findVariant();
    var base = v ? v.price : 0;
    var lc = letterCount();
    var letters = lc * letterRate();
    var lg = logoCount();
    var logos = lg * (PRICE.logo || 2650);
    var gq = glueQty();
    var glue = gq * (PRICE.glue || 364.99);
    var total = base + letters + logos + glue;
    setText('#hd-base', v ? money(base) : 'Unavailable');
    setRow('#hd-row-letters', lc > 0, lc + ' letter' + (lc === 1 ? '' : 's') + ' x ' + money(letterRate()), money(letters));
    setRow('#hd-row-logos', lg > 0, lg + ' logo' + (lg === 1 ? '' : 's') + ' x ' + money(PRICE.logo || 2650), money(logos));
    setRow('#hd-row-glue', gq > 0, gq + ' bucket' + (gq === 1 ? '' : 's') + ' x ' + money(PRICE.glue || 364.99), money(glue));
    setText('#hd-total', money(total));
    var atc = $('#hd-atc'); var err = $('#hd-unavailable');
    if (!v || !v.available) { if (atc) { atc.disabled = true; atc.textContent = 'Combination Unavailable'; } if (err) err.style.display = 'block'; }
    else { if (atc) { atc.disabled = false; atc.textContent = 'Add Halo to Cart - ' + money(total); } if (err) err.style.display = 'none'; }
    return { v: v, total: total, lc: lc, lg: lg, gq: gq };
  }
  function syncUI() {
    var wingBox = $('#hd-wings'); if (wingBox) wingBox.style.display = hasWings() ? 'block' : 'none';
    var letBox = $('#hd-letter-box'), logoBox = $('#hd-logo-box');
    if (letBox) letBox.style.display = state.mode === 'letter' ? 'block' : 'none';
    if (logoBox) logoBox.style.display = state.mode === 'logo' ? 'block' : 'none';
    var oc = $('#hd-outline-wrap'); if (oc) oc.style.display = state.twoColor ? 'flex' : 'none';
    setText('#hd-letter-count', letterCount() + ' letters x ' + money(letterRate()) + '/letter');
    setText('#hd-glue-note', state.glue ? ((isTen() ? 2 : 1) + ' x 4-gal bucket') : 'Not added');
    renderPreview();
    recalc();
  }

  // ============================================================
  //  WIRING
  // ============================================================
  function setActive(buttons, btn) { buttons.forEach(function (b) { b.classList.remove('active'); }); btn.classList.add('active'); }
  function wire() {
    var sizeBtns = $all('.hd-size-btn');
    sizeBtns.forEach(function (b) { b.addEventListener('click', function () { state.size = b.getAttribute('data-size'); setActive(sizeBtns, b); syncUI(); }); });
    var colorBtns = $all('.hd-color-btn');
    colorBtns.forEach(function (b) { b.addEventListener('click', function () { state.color = b.getAttribute('data-color'); setActive(colorBtns, b); syncUI(); }); });
    var padBtns = $all('.hd-pad-btn');
    padBtns.forEach(function (b) { b.addEventListener('click', function () { state.padding = b.getAttribute('data-pad'); setActive(padBtns, b); syncUI(); }); });
    var modeBtns = $all('.hd-mode-btn');
    modeBtns.forEach(function (b) { b.addEventListener('click', function () { state.mode = b.getAttribute('data-mode'); setActive(modeBtns, b); syncUI(); }); });
    var t = $('#hd-text'); if (t) t.addEventListener('input', function () {
      var val = t.value, out = '', letters = 0;
      for (var i = 0; i < val.length; i++) { var ch = val[i]; if (ch !== ' ') { if (letters >= 12) continue; letters++; } out += ch; }
      if (out !== val) { t.value = out; }
      state.text = out; syncUI();
    });
    var lcSel = $('#hd-letter-color'); if (lcSel) lcSel.addEventListener('change', function () { state.letterColor = lcSel.value; syncUI(); });
    var two = $('#hd-twocolor'); if (two) two.addEventListener('change', function () { state.twoColor = two.checked; syncUI(); });
    var ocSel = $('#hd-outline-color'); if (ocSel) ocSel.addEventListener('change', function () { state.outlineColor = ocSel.value; syncUI(); });
    var w1 = $('#hd-wing1'); if (w1) w1.addEventListener('change', function () { state.wingLogo1 = w1.checked; syncUI(); });
    var w3 = $('#hd-wing3'); if (w3) w3.addEventListener('change', function () { state.wingLogo3 = w3.checked; syncUI(); });
    var g = $('#hd-glue'); if (g) g.addEventListener('change', function () { state.glue = g.checked; syncUI(); });
    var atc = $('#hd-atc'); if (atc) atc.addEventListener('click', addToCart);
  }

  // ============================================================
  //  ADD TO CART
  // ============================================================
  function addToCart() {
    var r = recalc();
    if (!r.v || !r.v.available) return;
    var atc = $('#hd-atc');
    if (atc) { atc.disabled = true; atc.textContent = 'Adding...'; }
    var colorName = function (hex) { for (var i = 0; i < COLORS.length; i++) { if (COLORS[i].hex === hex) return COLORS[i].name; } return hex; };
    var designSummary =
      state.size + ' / ' + state.color + ' / ' + state.padding +
      ' | ' + (state.mode === 'logo' ? 'Main: Logo' : 'Lettering: "' + (state.text || '') + '" ' + colorName(state.letterColor) + (state.twoColor ? ' + ' + colorName(state.outlineColor) + ' outline' : '')) +
      (hasWings() ? ' | Wings: ' + (state.wingLogo1 ? '1st-logo ' : '') + (state.wingLogo3 ? '3rd-logo' : '') : '') +
      (r.gq > 0 ? ' | Glue: ' + r.gq + ' bucket(s)' : '');
    var ref = 'HALO-' + Date.now();
    var items = [{ id: r.v.id, quantity: 1, properties: { 'Halo Design': designSummary, '_haloRef': ref } }];
    if (r.lc > 0) {
      var letterVid = isTen() ? ADDON.letter10 : ADDON.letter5;
      if (letterVid) items.push({ id: letterVid, quantity: r.lc, properties: { 'For Halo': ref, 'Lettering': '"' + (state.text || '') + '" (' + depth() + "' halo)" } });
    }
    if (r.lg > 0 && ADDON.logo) items.push({ id: ADDON.logo, quantity: r.lg, properties: { 'For Halo': ref, 'Logos': r.lg } });
    if (r.gq > 0 && ADDON.glue) items.push({ id: ADDON.glue, quantity: r.gq, properties: { 'For Halo': ref } });
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw new Error(e.description || 'Cart error'); }); return res.json(); })
      .then(function () { if (atc) { atc.classList.add('added'); atc.textContent = 'Added to Cart'; } return fetch('/cart.js').then(function (r2) { return r2.json(); }); })
      .then(function (cart) {
        if (cart) { var els = document.querySelectorAll('.cart-count, #CartCount, .header-cart-count, [data-cart-count], .ajaxcart__count'); els.forEach(function (el) { el.textContent = cart.item_count; }); }
        setTimeout(function () { if (atc) { atc.classList.remove('added'); recalc(); } window.location.href = '/cart'; }, 700);
      })
      .catch(function (err) { if (atc) { atc.disabled = false; atc.textContent = 'Error - Retry'; } console.error('Halo add-to-cart error:', err); });
  }

  // ---- init ----
  function populateColorSelects() {
    if (!COLORS.length) return;
    ['#hd-letter-color', '#hd-outline-color'].forEach(function (sel) {
      var el = $(sel); if (!el) return; el.innerHTML = '';
      COLORS.forEach(function (c) { var o = document.createElement('option'); o.value = c.hex; o.textContent = c.name; el.appendChild(o); });
    });
    var lc = $('#hd-letter-color'); if (lc) lc.value = state.letterColor;
    var oc = $('#hd-outline-color'); if (oc) oc.value = state.outlineColor;
  }
  function applyDefaults() {
    populateColorSelects();
    $all('.hd-color-btn').forEach(function (b) {
      var c = b.getAttribute('data-color');
      if (COLOR_HEX[c]) b.style.background = COLOR_HEX[c];
      b.classList.toggle('active', c === state.color);
    });
    $all('.hd-size-btn').forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-size') === state.size); });
    $all('.hd-pad-btn').forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-pad') === state.padding); });
    $all('.hd-mode-btn').forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-mode') === state.mode); });
    var t = $('#hd-text'); if (t) t.value = state.text;
  }
  function init() { wire(); applyDefaults(); syncUI(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();